import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { KudoTipCard } from "@/components/crew/kudo-tip-card"
import InputError from "@/components/input-error"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useInitials } from "@/hooks/use-initials"
import { useOnline } from "@/hooks/use-online"
import { kudosCrewMembershipPath } from "@/routes"
import type { CrewMembershipDetail } from "@/types/crew"

/**
 * OKS-396 — Inline kudo composer on Brand/Crew/Show (UX Spec §2.A).
 *
 * Mirrors the proven OKS-26 `<CrewMemberNotesEditor>` form pattern:
 * Inertia `<Form>` render-prop + plain shadcn `Textarea`/`Label` +
 * `<InputError>`. **No shadcn Form / FormField / RHF / zod** (Crew rule).
 *
 * Flow: a brand-side user writes a ≤280-char message → Send POSTs to the
 * OKS-395 endpoint (`kudosCrewMembershipPath`, js-routes) → BE 303
 * redirect-back with flash `kudos.flash.sent` (toast via `useFlash` at
 * `PersistentLayout` — NOT mounted here) → `onSuccess` clears the field →
 * Inertia re-render prepends the new kudo into RECENT KUDOS.
 *
 * **Wire-format canary** (Vitest asserts the literal `name`): textarea
 * `name="kudo[message]"` — nests under BE's `params.require(:kudo)`. A
 * top-level `message` would trip the strong-param require and silently
 * fail. Errors render keyed `errors["kudo.message"]` (Inertia flattens
 * the nested-param namespace with a dot).
 *
 * Counter is always visible (280 is short; the limit is the point —
 * UX Spec §11). It is NOT hard-capped via `maxLength` so the user can
 * cross 280 and see the warning→error transition + hint; Send is
 * disabled past the limit and the server remains the source of truth.
 *
 * Visibility is gated UPSTREAM by `can_send_kudo` on the page — this
 * component assumes the caller is permitted to send.
 *
 * Locked testids:
 *   - `kudo-composer`         — wrapper
 *   - `kudo-composer-input`   — Textarea
 *   - `kudo-composer-counter` — `{n}/280` counter
 *   - `kudo-composer-submit`  — Send button
 *   - `kudo-composer-error`   — error region (`errors["kudo.message"]`)
 *   - `kudo-tip`              — owned by `<KudoTipCard>`
 */

const KUDO_MAX_LENGTH = 280

interface KudoComposerProps {
  membership: Pick<CrewMembershipDetail, "id" | "creator">
}

export function KudoComposer({ membership }: KudoComposerProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const initials = useInitials()
  const { creator } = membership

  const [message, setMessage] = useState("")
  const length = message.length
  const overLimit = length > KUDO_MAX_LENGTH
  const atLimit = length === KUDO_MAX_LENGTH
  const blank = message.trim().length === 0
  const canSubmit = !blank && !overLimit && online

  return (
    <div data-testid="kudo-composer" className="flex flex-col gap-3">
      {/* Mini-creator row — who you're recognising (read-only). */}
      <div className="flex items-center gap-2">
        <Avatar className="size-8 shrink-0">
          <AvatarFallback className="text-xs">
            {initials(creator.display_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">
            {creator.display_name}
          </span>
          <span className="text-muted-foreground truncate font-mono text-xs">
            @{creator.tiktok_handle}
          </span>
        </div>
      </div>

      <KudoTipCard />

      <Form
        action={kudosCrewMembershipPath(membership.id)}
        method="post"
        options={{ preserveScroll: true }}
        onSuccess={() => setMessage("")}
      >
        {({ errors, processing }) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor="kudo-composer-input">
              {t("crew.detail.kudos.textarea_label")}
            </Label>
            <Textarea
              id="kudo-composer-input"
              // **Wire-format canary** — `kudo[message]` nests under BE's
              // `params.require(:kudo)`. NEVER top-level `message`.
              name="kudo[message]"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("crew.detail.kudos.placeholder")}
              disabled={processing}
              aria-describedby="kudo-composer-counter kudo-composer-error"
              aria-invalid={
                overLimit || errors["kudo.message"] ? "true" : undefined
              }
              data-testid="kudo-composer-input"
              className="resize-y"
            />
            <p
              id="kudo-composer-counter"
              data-testid="kudo-composer-counter"
              className={
                "self-end text-xs tabular-nums " +
                (overLimit
                  ? "text-red-600 dark:text-red-400"
                  : atLimit
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-muted-foreground")
              }
            >
              {t("crew.detail.kudos.counter_format", {
                count: length,
                limit: KUDO_MAX_LENGTH,
              })}
            </p>
            <div
              id="kudo-composer-error"
              data-testid="kudo-composer-error"
              role="alert"
              aria-live="polite"
            >
              {/* Client-side over-limit hint (icon-free text — color is
                  NOT the sole signal, a11y §6). Server validation text
                  surfaces via the nested-namespace `errors["kudo.message"]`
                  envelope below. */}
              {overLimit && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t("crew.detail.kudos.over_limit_hint", {
                    limit: KUDO_MAX_LENGTH,
                  })}
                </p>
              )}
              <InputError messages={errors["kudo.message"]} />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!canSubmit || processing}
                data-testid="kudo-composer-submit"
                className="w-full sm:w-auto"
              >
                {processing
                  ? t("crew.detail.kudos.sending")
                  : t("crew.detail.kudos.send")}
              </Button>
            </div>
          </div>
        )}
      </Form>
    </div>
  )
}
